import { copaService } from 'js/vue/services/copa.service'
import { ref, watch } from 'vue'
import { NotifyAlert } from '../boot/Notify'
import { paramsJSON } from 'store/params.store'
import {
  copaLoading,
  levelAccess,
  tierList,
  comparationItems,
  activeFilter,
  availableFilters,
  filtersLoading,
  filterModel,
  enablers,
  summaryCards,
  summaryCardsLoading,
  filterParams,
  evolutionSeries,
  evolutionSeriesLoading,
  quickWinsHighlightsData,
  quickWinsHighlightsLoading,
} from 'store/copa.stores'

export default function useCopaReports() {
  const {
    getAuth,
    getReportsOverview,
    getReportsEnablers,
    getReportsSummary,
    getReportsEvolution,
    getReportsQuickWins,
    getReportsHighlights,
    getZonesList,
    getBottlersList,
    getTierList,
  } = copaService()

  const formatFloatNumber = (num) =>
    Number.isInteger(num)
      ? num
      : Number(num).toFixed(1).replace('.', ',')

  const formatOverviewRows = (items) =>
    items.map((item) => ({
      category: item.name,
      possible_points: formatFloatNumber(item.peso),
      current_points: formatFloatNumber(item.value),
      gap_oportunity: formatFloatNumber(item.gap_peso),
      gap_bench_latam: formatFloatNumber(item.gap_latam),
    }))

  async function fetchOverview(params) {
    try {
      const response = await getReportsOverview(params)
      const result = response.results.at()

      if (!result) return

      return {
        name: result.fab_name,
        fabName: result.latam_fab_name,
        rows: formatOverviewRows(result.items),
      }
    } catch (error) {
      console.error('Error fetching overview:', error)
    }
  }

  async function fetchEnablers(params = filterParams.value) {
    try {
      const response = await getReportsEnablers(params)
      enablers.value = response?.results || []
    } catch (error) {
      console.error('Error fetching enablers:', error)
    }
  }

  async function fetchFiltersOptions(params) {
    try {
      filtersLoading.value = true

      const zonesResponse = await getZonesList(params)
      const bottlersResponse = await getBottlersList(params)

      const zones = zonesResponse?.results?.map((i) => ({
        label: i.fab_zone,
        value: i.fab_zone,
        filter: 'fab_zone',
      }))

      const bottlers = bottlersResponse?.results?.map((i) => ({
        label: i.fab_name,
        value: i.fab_code,
        filter: 'fab_code',
      }))

      const both = [...(zones || []), ...(bottlers || [])]

      filtersLoading.value = false

      return { zones, bottlers, both }
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  const formatSummaryCards = (items) =>
    items.map(({ key, value, title, subtitle, icon }) => ({
      key,
      title,
      subtitle,
      icon,
      value:
        typeof value === 'number' ? formatFloatNumber(value) : value,
    }))

  async function fetchSummary(params = filterParams.value) {
    try {
      summaryCardsLoading.value = true
      const response = await getReportsSummary(params)
      const result = response.results.at()

      if (!result) {
        summaryCards.value = []
        summaryCardsLoading.value = false
        throw new Error('No summary data found')
      }

      if (result.items.length > 4) {
        const filteredItems = result.items.filter(
          (i) => i.key !== 'min'
        )
        summaryCards.value = formatSummaryCards(filteredItems) || []
      } else {
        summaryCards.value = formatSummaryCards(result.items) || []
      }

      summaryCardsLoading.value = false
    } catch (error) {
      console.error('Error fetching summary:', error)
    }
  }

  const extractEvolutionData = (response) =>
    response?.results?.map((r) => {
      const value = r.items?.[0]?.value
      return value.toFixed(1) || 0
    }) || []

  async function fetchEvolution(params = filterParams.value) {
    try {
      evolutionSeriesLoading.value = true

      const series = []
      const {
        fab_tier,
        fab_tier_type,
        fab_zone,
        fab_code,
        bottler,
        both_tier_types,
      } = params

      if (fab_code) {
        const response = await getReportsEvolution({ fab_code })
        series.push({
          name: bottler,
          data: extractEvolutionData(response),
        })
      } else if (fab_zone) {
        const response = await getReportsEvolution({ fab_zone })
        series.push({
          name: fab_zone,
          data: extractEvolutionData(response),
        })
      } else if (fab_tier === 2) {
        const response = await getReportsEvolution({ fab_tier: 2 })
        series.push({
          name: 'W/O SOV',
          data: extractEvolutionData(response),
        })
      } else if (fab_tier === 1 && both_tier_types) {
        const [responseA, responseB] = await Promise.all([
          getReportsEvolution({ fab_tier: 1, fab_tier_type: 'A' }),
          getReportsEvolution({ fab_tier: 1, fab_tier_type: 'B' }),
        ])

        series.push(
          {
            name: 'MMUC > 150',
            data: extractEvolutionData(responseA),
          },
          {
            name: 'MMUC <= 150',
            data: extractEvolutionData(responseB),
          }
        )
      } else if (fab_tier === 1 && !fab_tier_type) {
        const response = await getReportsEvolution({
          fab_tier: 1,
        })
        series.push({
          name: 'MMUC',
          data: extractEvolutionData(response),
        })
      } else if (fab_tier === 1 && fab_tier_type === 'A') {
        const response = await getReportsEvolution({
          fab_tier: 1,
          fab_tier_type: 'A',
        })
        series.push({
          name: 'MMUC > 150',
          data: extractEvolutionData(response),
        })
      } else if (fab_tier === 1 && fab_tier_type === 'B') {
        const response = await getReportsEvolution({
          fab_tier: 1,
          fab_tier_type: 'B',
        })
        series.push({
          name: 'MMUC <= 150',
          data: extractEvolutionData(response),
        })
      }

      evolutionSeries.value = series.map((s) => ({
        ...s,
        fixed: true,
      }))

      evolutionSeriesLoading.value = false
    } catch (error) {
      console.error('Error fetching evolution:', error)
    }
  }

  async function fetchQuickWinsHighlights(
    fabCode,
    isPriority = false
  ) {
    try {
      const [quickWinsResponse, highlightsResponse] =
        await Promise.all([
          getReportsQuickWins({ fab_code: fabCode }),
          getReportsHighlights({ fab_code: fabCode }),
        ])

      return {
        fab_code: fabCode,
        fab_name: quickWinsResponse?.fab_name || '',
        current_points:
          quickWinsResponse?.current_points?.toFixed(1) || 0,
        future_points:
          quickWinsResponse?.future_points?.toFixed(1) || 0,
        quickWins: quickWinsResponse?.items || [],
        highlights: highlightsResponse || [],
        isPriority,
      }
    } catch (error) {
      console.error(
        'Error fetching quick wins and highlights:',
        error
      )
      return null
    }
  }

  async function updateQuickWinsHighlights(
    dashboardFabCode = null,
    isPriority = false,
    fabZone = null
  ) {
    quickWinsHighlightsLoading.value = true

    try {
      if (!isPriority && fabZone) {
        quickWinsHighlightsData.value = [
          {
            fab_code: null,
            fab_zone: fabZone,
            fab_name: null,
            isPriority: false,
            showCards: false,
          },
        ]
        return
      }

      if (!dashboardFabCode) {
        quickWinsHighlightsData.value = []
        return
      }

      if (dashboardFabCode) {
        const data = await fetchQuickWinsHighlights(
          dashboardFabCode,
          isPriority
        )
        if (data) quickWinsHighlightsData.value = [data]
      }
    } catch (error) {
      console.error(
        'Error updating quick wins and highlights:',
        error
      )
    } finally {
      quickWinsHighlightsLoading.value = false
    }
  }

  const search = ref('')

  const isFilterDisabled = (filter) => {
    if (filter === 'Zona') return !availableFilters.value.zones
    if (filter === 'Bottler') return !availableFilters.value.bottlers
    return false
  }

  const loadItems = async () => {
    const response = await fetchFiltersOptions({
      search: search.value,
      fab_tier: filterParams.value.fab_tier,
      fab_tier_type: filterParams.value.fab_tier_type,
    })

    availableFilters.value.zones =
      response?.zones && response.zones.length > 0
    availableFilters.value.bottlers =
      response?.bottlers && response.bottlers.length > 0

    if (
      activeFilter.value === 'Zona' &&
      !availableFilters.value.zones
    ) {
      activeFilter.value = 'Tudo'
    } else if (
      activeFilter.value === 'Bottler' &&
      !availableFilters.value.bottlers
    ) {
      activeFilter.value = 'Tudo'
    }

    if (activeFilter.value === 'Tudo') {
      comparationItems.value = response?.both || []
    } else if (activeFilter.value === 'Zona') {
      comparationItems.value = response?.zones || []
    } else if (activeFilter.value === 'Bottler') {
      comparationItems.value = response?.bottlers || []
    }
  }

  async function addComparisonEvolution(item) {
    try {
      const existingItem = evolutionSeries.value.find(
        (s) => s.name === item.label
      )
      if (existingItem) {
        NotifyAlert(`${item.label} já está sendo comparado.`)
        return false
      }

      const comparisonCount = evolutionSeries.value.filter(
        (s) => !s.fixed
      ).length
      if (comparisonCount >= 5) {
        NotifyAlert('Limite máximo de 5 comparações atingido.')
        return false
      }

      const params = { [item.filter]: item.value }
      const response = await getReportsEvolution(params)

      evolutionSeries.value.push({
        name: item.label,
        data: extractEvolutionData(response),
        fixed: false,
      })

      return true
    } catch (error) {
      console.error('Error adding comparison evolution:', error)
      return false
    }
  }

  async function addComparisonEvolutionBest(type) {
    try {
      const fabZone =
        filterModel.value.fabricante?.fab_zone ||
        filterModel.value.zona?.fab_zone ||
        ''

      const finalParams = {
        fab_tier: filterModel.value.tier,
        fab_code_best: '',
      }

      if (type === 'Melhor da região') {
        finalParams.fab_zone = fabZone
      }

      const comparisonCount = evolutionSeries.value.filter(
        (s) => !s.fixed
      ).length

      if (comparisonCount >= 5) {
        NotifyAlert('Limite máximo de 5 comparações atingido.')
        return {}
      }

      const response = await getReportsEvolution(finalParams)
      const fabName = response.results.at()?.fab_name

      const existingItem = evolutionSeries.value.find(
        (s) => s.name === fabName
      )
      if (existingItem) {
        NotifyAlert(`${type} já está sendo comparado.`)
        return {}
      }

      evolutionSeries.value.push({
        name: fabName || 'Comparação',
        data: extractEvolutionData(response),
        fixed: false,
      })

      return { fabName }
    } catch (error) {
      console.error('Error adding comparison evolution best:', error)
      return false
    }
  }

  function removeComparisonEvolution(index) {
    if (index !== undefined) {
      if (
        evolutionSeries.value[index] &&
        !evolutionSeries.value[index].fixed
      ) {
        evolutionSeries.value.splice(index, 1)
      }
    } else {
      evolutionSeries.value = evolutionSeries.value.filter(
        (s) => s.fixed
      )
    }
  }

  watch([activeFilter, search], () => {
    loadItems()
  })

  async function refreshDashboard(cardRef = null) {
    const params = { ...filterParams.value }

    fetchTierList()
    fetchSummary(params)
    fetchEvolution(params)
    fetchEnablers(params)

    const dashboardFabCode = params.fab_code || null
    const hasFabZoneOnly = params.fab_zone && !params.fab_code
    const isPriority = !hasFabZoneOnly && !!dashboardFabCode

    if (dashboardFabCode || hasFabZoneOnly) {
      updateQuickWinsHighlights(
        dashboardFabCode,
        isPriority,
        params.fab_zone
      )
    }

    setTimeout(() => {
      cardRef?.value?.fetchInitialData(params)
    }, 500)
  }

  async function requestAuth() {
    try {
      const response = await getAuth(paramsJSON.value)
      if (response?.level) {
        levelAccess.value = Number(response.level)
      }
    } catch (error) {
      console.error('Error requesting auth:', error)
    } finally {
      copaLoading.value = false
    }
  }

  async function fetchTierList() {
    try {
      const response = await getTierList({ t: 'global' })
      tierList.value = response?.results || []

      const findItem = (field, value) =>
        tierList.value.find((i) => i[field] === value)

      if (findItem('fab_tier', 1)) {
        filterModel.value.tier = 1

        if (
          findItem('fab_tier_type', 'A') &&
          findItem('fab_tier_type', 'B')
        ) {
          return
        }

        if (findItem('fab_tier_type', 'A')) {
          filterModel.value.type_a = true
        } else if (findItem('fab_tier_type', 'B')) {
          filterModel.value.type_b = true
        }
      } else if (findItem('fab_tier', 2)) {
        filterModel.value.tier = 2
      }
    } catch (error) {
      console.error('Error requesting tier list:', error)
    }
  }

  async function selectBottlerForQuickWins(fabCode, itemIndex = 0) {
    if (!fabCode) return

    quickWinsHighlightsLoading.value = true

    try {
      const data = await fetchQuickWinsHighlights(fabCode, false)

      if (data && quickWinsHighlightsData.value[itemIndex]) {
        quickWinsHighlightsData.value[itemIndex] = {
          ...quickWinsHighlightsData.value[itemIndex],
          ...data,
          showCards: true,
        }
      }
    } catch (error) {
      console.error('Error selecting bottler:', error)
    } finally {
      quickWinsHighlightsLoading.value = false
    }
  }

  return {
    enablers,

    fetchOverview,
    fetchEnablers,
    fetchFiltersOptions,
    fetchSummary,
    fetchEvolution,
    fetchQuickWinsHighlights,
    updateQuickWinsHighlights,
    selectBottlerForQuickWins,
    addComparisonEvolution,
    addComparisonEvolutionBest,
    removeComparisonEvolution,
    refreshDashboard,
    requestAuth,

    search,
    isFilterDisabled,
    loadItems,
  }
}
